import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Input from "@/components/ui/input";
import Dropdown from "@/components/ui/dropdown";
import Button from "@/components/ui/button";
import ConfirmModal from "@/components/ui/confirm-modal";
import Chip from "@/components/ui/chip";
import { ArrowDown, Plus, Delete, Close } from "@/components/icons";
import { PERMISSION_MATRIX } from "../config/permission-matrix";
import { resolvePermissionWithDependencies } from "../config/permission-dependencies";
import AssignByTitleModal from "../components/AssignByTitleModal";
import PermissionDependencyBadge from "../components/PermissionDependencyBadge";
import DependencyInfoPanel from "../components/DependencyInfoPanel";
import { getRoleDisplayName } from "../config/role-display-names";
import {
  extractApiErrorMessage,
  rolesAndPermissionsApi,
} from "../services/roles-and-permissions.api";
import {
  isLearnerRestrictedRoleType,
  isPermissionAllowedForRoleType,
  stripDisallowedForRoleType,
} from "../config/learner-permissions";
import {
  canActorAssignRoleType,
  canActorChangeRoleType,
} from "../config/role-type-policy";
import { getSessionRoleTypes } from "@/core/auth/utils/session-role-types";
import { UserRoleType, type Role } from "../types/roles-and-permissions.types";
import { usePermission } from "@/core/permissions/hooks";
import { fetchCurrentUser, useStore } from "@/store";
import { PERMISSIONS } from "@/core/permissions/config";
import { isEducationManagerSession } from "@/core/auth/utils/education-manager-session";
import {
  filterMatrixForEducationManager,
  stripForbiddenForEducationManager,
} from "@/core/permissions/education-manager-restrictions";
import styles from "./RolesAndPermissionsPage.module.scss";

const NEW_ROLE_ID = "__new__";

interface EditingRole {
  id: string;
  name: string;
  description: string;
  type: number;
  permissionKeys: Set<string>;
  isNew: boolean;
  isDefault?: boolean;
  /** Sunucudaki (kaydedilmiş) rol tipi; tip değişikliği kontrolü için. */
  originalType?: number;
}

const RolesAndPermissionsPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();

  const canCreate = hasPermission(PERMISSIONS.ROLES_AND_PERMISSIONS_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.ROLES_AND_PERMISSIONS_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.ROLES_AND_PERMISSIONS_DELETE);

  // Eğitim Yöneticisi rolü hem kendi UI'sında hem oluşturduğu rollerde
  // yasaklı yetkileri (USERS.CREATE/DELETE/UPDATE_PASSWORD, AUDIT_LOGS.*)
  // veremez — matrisi bu kullanıcılar için filtreliyoruz.
  const userType = useStore((s) => s.userType);
  const currentUser = useStore((s) => s.currentUser);
  const isEduMgr = useMemo(
    () => isEducationManagerSession(userType, currentUser),
    [userType, currentUser],
  );
  // Oturumdaki tüm rol tipleri (token). Rol tipi atama/değiştirme kısıtı için.
  const actorRoleTypes = useMemo(
    () => getSessionRoleTypes(),
    // userType değişimi = yeniden giriş/tazelenen oturum
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userType, currentUser],
  );
  const availableMatrix = useMemo(
    () =>
      isEduMgr
        ? filterMatrixForEducationManager(PERMISSION_MATRIX)
        : PERMISSION_MATRIX,
    [isEduMgr],
  );
  const resolveDeps = useCallback(
    (key: string): string[] => {
      const resolved = resolvePermissionWithDependencies(key);
      return isEduMgr ? stripForbiddenForEducationManager(resolved) : resolved;
    },
    [isEduMgr],
  );

  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [roleSearch, setRoleSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditingRole | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);
  /** "Ünvana göre tanımla" modalının hedef rolü; null iken modal kapalıdır. */
  const [assignTitleTarget, setAssignTitleTarget] = useState<Role | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [permissionSearch, setPermissionSearch] = useState("");
  const [dependencyInfoOpen, setDependencyInfoOpen] = useState(false);
  // Aynı anda yalnızca tek kategori açık olur: birden fazla kart aynı anda
  // genişlediğinde matris çok uzuyor ve sticky sidebar ile birlikte sayfa
  // kaydırması zorlaşıyordu.
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(
    null,
  );

  // Matris konteynerinin genişliğine göre kart kolon sayısını ayarlıyoruz.
  // CSS multi-column + overflow scroll kombinasyonu kartları dikey scroll'da
  // gizliyordu; onun yerine sabit N kolon flex-stack kullanıyoruz.
  const matrixRef = useRef<HTMLDivElement>(null);
  const [matrixColumnCount, setMatrixColumnCount] = useState(2);

  useEffect(() => {
    const node = matrixRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const update = (width: number) => {
      // Maks 2 kolon: dar ekranda 1, geri kalan tüm genişliklerde 2.
      const next = width < 520 ? 1 : 2;
      setMatrixColumnCount((prev) => (prev === next ? prev : next));
    };
    update(node.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        update(entry.contentRect.width);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [editing?.id]);

  const allRoleTypeOptions = useMemo(
    () =>
      Object.entries(UserRoleType)
        .filter(([, v]) => typeof v === "number")
        .map(([key, value]) => ({
          label: t(`rolesAndPermissions.roleTypes.${key}`, key),
          value: String(value),
        })),
    [t],
  );
  /** Actor'ın atayabileceği rol tipleri (+ mevcut tip, etiket gösterimi için). */
  const getRoleTypeOptions = useCallback(
    (currentType: number) =>
      allRoleTypeOptions.filter(
        (o) =>
          o.value === String(currentType) ||
          canActorAssignRoleType(actorRoleTypes, Number(o.value)),
      ),
    [allRoleTypeOptions, actorRoleTypes],
  );

  const getDefaultPermissionsForType = useCallback(
    (type: number): Set<string> => {
      const defaultRole = roles.find(
        (r) => r.isDefault && r.type === type && r.permissionKeys,
      );
      if (!defaultRole?.permissionKeys) return new Set();
      const keys = defaultRole.permissionKeys
        .split(/,\s*/)
        .map((k) => k.trim())
        .filter(Boolean);
      // Eğitim Yöneticisi başkasına yasaklı yetkileri devredemez:
      // varsayılan rol şablonundan da bu key'ler ayıklanır.
      return new Set(isEduMgr ? stripForbiddenForEducationManager(keys) : keys);
    },
    [roles, isEduMgr],
  );

  const getRoleTypeLabel = useCallback(
    (type?: number) => {
      if (type === undefined || type === null) return "";
      const key = UserRoleType[type];
      return key ? t(`rolesAndPermissions.roleTypes.${key}`, key) : "";
    },
    [t],
  );

  const fetchRoles = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await rolesAndPermissionsApi.getAll({
        page: 1,
        pageSize: 100,
        filters: { sortBy: "name", sortOrder: "asc" },
      });
      setRoles(response.data);
      return response.data;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t("rolesPage.rolesLoadFailed"),
      );
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchRoles().then((list) => {
      if (list.length > 0 && !selectedId) {
        setSelectedId(list[0].id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setEditing(null);
      return;
    }
    if (selectedId === NEW_ROLE_ID) {
      const defaultType = UserRoleType.USER;
      setEditing({
        id: NEW_ROLE_ID,
        name: "",
        description: "",
        type: defaultType,
        // Varsayılan Kursiyer şablonu kirli olabilir; izin listesine indirgenir.
        permissionKeys: stripDisallowedForRoleType(
          getDefaultPermissionsForType(defaultType),
          defaultType,
        ),
        isNew: true,
      });
      return;
    }
    setIsLoadingDetail(true);
    rolesAndPermissionsApi
      .getById(selectedId)
      .then((detail) => {
        // Detail endpoint bazen isDefault dönmediği için liste yanıtındaki
        // değere de bakıyoruz; aksi halde varsayılan rolün düzenleme/silme
        // korumaları (UI'da disable + handler guard'ları) bypass'lanabiliyor.
        const listEntry = roles.find((r) => r.id === detail.id);
        setEditing({
          id: detail.id,
          name: detail.roleName ?? "",
          description: detail.roleDescription ?? "",
          type: detail.type ?? UserRoleType.USER,
          originalType: detail.type ?? UserRoleType.USER,
          permissionKeys: new Set(detail.permissionKeys ?? []),
          isNew: false,
          isDefault: detail.isDefault ?? listEntry?.isDefault ?? false,
        });
      })
      .catch((err) => {
        toast.error(
          err instanceof Error ? err.message : t("rolesPage.roleLoadFailed"),
        );
        setSelectedId(null);
      })
      .finally(() => setIsLoadingDetail(false));
  }, [selectedId]);

  const filteredRoles = useMemo(() => {
    const q = roleSearch.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter(
      (r) =>
        r.roleName.toLowerCase().includes(q) ||
        getRoleDisplayName(r.roleName).toLowerCase().includes(q) ||
        (r.roleDescription || "").toLowerCase().includes(q),
    );
  }, [roles, roleSearch]);

  const translatedMatrix = useMemo(
    () =>
      availableMatrix.map((cat) => ({
        ...cat,
        label: t(`rolesAndPermissions.matrix.categories.${cat.id}`, cat.label),
        permissions: cat.permissions.map((p) => {
          const action = p.key.split(".").pop() ?? "";
          return {
            ...p,
            label: t(`rolesAndPermissions.matrix.actions.${action}`, p.label),
          };
        }),
      })),
    [t, availableMatrix],
  );

  /** Bağımlılık badge'inin kendi key -> etiket lookup'u için düz bir map. */
  const keyToLabel = useMemo(() => {
    const map: Record<string, string> = {};
    for (const cat of translatedMatrix) {
      for (const p of cat.permissions) {
        map[p.key] = p.label;
      }
    }
    return map;
  }, [translatedMatrix]);
  const getPermissionLabel = useCallback(
    (key: string) => keyToLabel[key] ?? key,
    [keyToLabel],
  );

  const filteredMatrix = useMemo(() => {
    const q = permissionSearch.trim().toLowerCase();
    if (!q) return translatedMatrix;
    return translatedMatrix
      .map((cat) => {
        const catMatch = cat.label.toLowerCase().includes(q);
        if (catMatch) return cat;
        const filteredPerms = cat.permissions.filter(
          (p) =>
            p.label.toLowerCase().includes(q) ||
            p.key.toLowerCase().includes(q),
        );
        if (filteredPerms.length === 0) return null;
        return { ...cat, permissions: filteredPerms };
      })
      .filter((c): c is NonNullable<typeof c> => c !== null);
  }, [permissionSearch, translatedMatrix]);

  // Kategorileri kolonlara round-robin dağıt: her kolon kabaca eşit sayıda kart
  // alır ve bir kart açıldığında sadece kendi kolonundaki alttaki kartlar kayar.
  const matrixColumns = useMemo(() => {
    const cols: (typeof filteredMatrix)[] = Array.from(
      { length: matrixColumnCount },
      () => [],
    );
    filteredMatrix.forEach((cat, idx) => {
      cols[idx % matrixColumnCount].push(cat);
    });
    return cols;
  }, [filteredMatrix, matrixColumnCount]);

  // Rollerde matriste karşılığı olmayan sistem yetkileri de bulunur
  // (NOTIFICATIONS.*, DEVICE_TOKENS.*, kaldırılmış SMS_BROADCAST.* vb.).
  // Sayaç bunları da saydığında hiçbir kutu işaretli değilken "12/80" gibi
  // görünmeyen yetkiler gösteriyordu; yalnızca matristeki key'ler sayılır.
  const matrixKeys = useMemo(
    () =>
      new Set(availableMatrix.flatMap((c) => c.permissions.map((p) => p.key))),
    [availableMatrix],
  );
  const totalSelectedCount = useMemo(
    () =>
      editing
        ? [...editing.permissionKeys].filter((k) => matrixKeys.has(k)).length
        : 0,
    [editing, matrixKeys],
  );
  const totalPermCount = matrixKeys.size;

  const togglePermission = (key: string) => {
    if (!editing) return;
    // Öğrenen rol tipinde (3/5) izin listesi dışı izin açılamaz; kapatmaya izin var.
    if (
      !editing.permissionKeys.has(key) &&
      !isPermissionAllowedForRoleType(key, editing.type)
    ) {
      return;
    }
    setEditing({
      ...editing,
      permissionKeys: (() => {
        const next = new Set(editing.permissionKeys);
        if (next.has(key)) {
          next.delete(key);
        } else {
          // Auto-include transitive dependencies when turning a permission on.
          resolveDeps(key)
            .filter((k) => isPermissionAllowedForRoleType(k, editing.type))
            .forEach((k) => next.add(k));
        }
        return next;
      })(),
    });
  };

  const toggleCategory = (categoryKeys: string[]) => {
    if (!editing) return;
    // Öğrenen rol tipinde yalnızca izin listesindeki key'ler toplu seçilir.
    const keys = categoryKeys.filter((k) =>
      isPermissionAllowedForRoleType(k, editing.type),
    );
    if (keys.length === 0) return;
    const allSelected = keys.every((k) => editing.permissionKeys.has(k));
    const next = new Set(editing.permissionKeys);
    if (allSelected) {
      keys.forEach((k) => next.delete(k));
    } else {
      keys.forEach((k) => {
        resolveDeps(k)
          .filter((dep) => isPermissionAllowedForRoleType(dep, editing.type))
          .forEach((dep) => next.add(dep));
      });
    }
    setEditing({ ...editing, permissionKeys: next });
  };

  const toggleAll = () => {
    if (!editing) return;
    const selectableKeys = [...matrixKeys].filter((k) =>
      isPermissionAllowedForRoleType(k, editing.type),
    );
    const hasAll = selectableKeys.every((k) => editing.permissionKeys.has(k));
    // Matriste görünmeyen sistem yetkileri (bildirim, cihaz token'ı vb.)
    // toplu seçimden etkilenmez; aksi halde "Tümünü kaldır" bunları da silip
    // rolün bildirim gibi temel akışlarını sessizce bozuyordu.
    const hidden = [...editing.permissionKeys].filter(
      (k) => !matrixKeys.has(k),
    );
    setEditing({
      ...editing,
      permissionKeys: new Set(hasAll ? hidden : [...hidden, ...selectableKeys]),
    });
  };

  const toggleExpand = (id: string) => {
    setExpandedCategoryId((prev) => (prev === id ? null : id));
  };

  const handleSave = async () => {
    if (!editing) return;
    if (editing.isDefault && !editing.isNew) return;
    const name = editing.name.trim();
    if (!name) {
      toast.error(t("rolesAndPermissions.nameRequired"));
      return;
    }
    if (
      editing.isNew
        ? !canActorAssignRoleType(actorRoleTypes, editing.type)
        : editing.originalType !== undefined &&
          editing.type !== editing.originalType &&
          (!canActorChangeRoleType(actorRoleTypes, editing.originalType) ||
            !canActorAssignRoleType(actorRoleTypes, editing.type))
    ) {
      toast.error(t("rolesAndPermissions.roleTypeChangeNotAllowed"));
      return;
    }
    setIsSaving(true);
    try {
      // Öğrenen rol tipinde (3/5) izin listesi dışı izinler backend'de 400
      // döner; kaydetmeden önce ayıklanır (eski/kirli kayıtlar dahil).
      const permissionKeys = Array.from(
        stripDisallowedForRoleType(editing.permissionKeys, editing.type),
      ).join(",");
      const description = editing.description.trim();
      if (editing.isNew) {
        const created = await rolesAndPermissionsApi.create({
          name,
          description: description || undefined,
          type: editing.type,
          permissionKeys,
          isDefault: false,
        });
        toast.success(t("rolesAndPermissions.createSuccess"));
        await fetchRoles();
        setSelectedId(created.id);
      } else {
        await rolesAndPermissionsApi.update(editing.id, {
          name,
          description,
          type: editing.type,
          permissionKeys,
        });
        toast.success(t("rolesAndPermissions.updateSuccess"));
        await fetchRoles();
      }
      await fetchCurrentUser(true);
    } catch (err) {
      // Backend mesajı (örn. PERMISSION_NOT_ALLOWED_FOR_ROLE_TYPE) gösterilir.
      toast.error(
        extractApiErrorMessage(err, t("rolesAndPermissions.saveFailed")),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.isDefault) {
      toast.error(t("rolesAndPermissions.cannotDeleteDefault"));
      setDeleteTarget(null);
      return;
    }
    setIsDeleting(true);
    try {
      await rolesAndPermissionsApi.delete(deleteTarget.id);
      toast.success(t("rolesAndPermissions.deleteSuccess"));
      const wasSelected = selectedId === deleteTarget.id;
      setDeleteTarget(null);
      const list = await fetchRoles();
      if (wasSelected) {
        setSelectedId(list[0]?.id ?? null);
      }
      await fetchCurrentUser(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Rol silinemedi.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddNew = () => {
    if (!canCreate) return;
    setSelectedId(NEW_ROLE_ID);
  };

  const renderRoleCard = (role: Role) => {
    const isActive = selectedId === role.id;
    return (
      <button
        key={role.id}
        type="button"
        className={`${styles.roleCard} ${isActive ? styles.roleCardActive : ""}`}
        onClick={() => setSelectedId(role.id)}
      >
        <div className={styles.roleCardTop}>
          <span className={styles.roleCardName}>
            {getRoleDisplayName(role.roleName) || role.roleName}
          </span>
          {role.isDefault && (
            <Chip size="small" type="info" variant="outlined">
              {t("rolesAndPermissions.default")}
            </Chip>
          )}
        </div>
        {role.roleDescription?.trim() && (
          <p className={styles.roleCardDesc}>{role.roleDescription}</p>
        )}
        <div className={styles.roleCardMeta}>
          <span className={styles.roleCardType}>
            {getRoleTypeLabel(role.type)}
          </span>
          <span className={styles.roleCardCount}>
            {role.assignedPersonCount} {t("rolesAndPermissions.users")}
          </span>
        </div>
      </button>
    );
  };

  const renderDetail = () => {
    if (!editing) {
      return (
        <div className={styles.emptyState}>
          <p>{t("rolesAndPermissions.selectRole")}</p>
        </div>
      );
    }
    const currentRole = roles.find((r) => r.id === editing.id);
    const roleTypeOptions = getRoleTypeOptions(editing.type);
    // Rol tipi değişikliği yalnızca rol yöneticilerine (EDUCATION_MANAGER /
    // CENTRAL_MANAGER) açık; diğerleri yalnızca öğrenen tipinde rol oluşturabilir.
    const roleTypeLocked =
      editing.isDefault ||
      (!canUpdate && !editing.isNew) ||
      (!editing.isNew &&
        !canActorChangeRoleType(
          actorRoleTypes,
          editing.originalType ?? editing.type,
        ));
    const isLearnerType = isLearnerRestrictedRoleType(editing.type);
    const disallowedSelectedCount = isLearnerType
      ? [...editing.permissionKeys].filter(
          (k) => !isPermissionAllowedForRoleType(k, editing.type),
        ).length
      : 0;
    return (
      <div className={styles.detailPanel}>
        <div className={styles.detailHeader}>
          <div className={styles.detailHeaderLeft}>
            <span className={styles.detailEyebrow}>
              {editing.isNew
                ? t("rolesAndPermissions.newRole")
                : t("rolesAndPermissions.editRole")}
            </span>
            <Input
              value={editing.name}
              maxLength={100}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              placeholder={t("rolesAndPermissions.form.roleNamePlaceholder")}
              disabled={editing.isDefault || (!canUpdate && !editing.isNew)}
            />
            <textarea
              className={styles.roleDescriptionInput}
              value={editing.description}
              maxLength={500}
              rows={2}
              onChange={(e) =>
                setEditing({ ...editing, description: e.target.value })
              }
              placeholder={t(
                "rolesAndPermissions.form.roleDescriptionPlaceholder",
              )}
              disabled={editing.isDefault || (!canUpdate && !editing.isNew)}
            />
          </div>
          <div className={styles.detailHeaderRight}>
            <Dropdown
              label={t("rolesAndPermissions.form.roleType")}
              placeholder={t("rolesAndPermissions.form.roleTypePlaceholder")}
              options={roleTypeOptions}
              value={String(editing.type)}
              selectedLabel={
                roleTypeOptions.find((o) => o.value === String(editing.type))
                  ?.label
              }
              onSelect={(v) => {
                const nextType = Number(v);
                if (!canActorAssignRoleType(actorRoleTypes, nextType)) return;
                if (editing.isNew) {
                  setEditing({
                    ...editing,
                    type: nextType,
                    permissionKeys: stripDisallowedForRoleType(
                      getDefaultPermissionsForType(nextType),
                      nextType,
                    ),
                  });
                } else {
                  setEditing({
                    ...editing,
                    type: nextType,
                    permissionKeys: stripDisallowedForRoleType(
                      editing.permissionKeys,
                      nextType,
                    ),
                  });
                }
              }}
              disabled={roleTypeLocked}
            />
          </div>
        </div>

        {isLearnerType && (
          <div className={styles.learnerRestrictionNote} role="note">
            {t("rolesAndPermissions.learnerRestriction.info")}
            {disallowedSelectedCount > 0 && (
              <strong>
                {" "}
                {t("rolesAndPermissions.learnerRestriction.willBeRemoved", {
                  count: disallowedSelectedCount,
                })}
              </strong>
            )}
          </div>
        )}

        {currentRole && !editing.isNew && (
          <div className={styles.detailStats}>
            <span>
              {currentRole.assignedPersonCount} {t("rolesAndPermissions.users")}
            </span>
            {/* Toplu atama yalnızca KAYDEDİLMİŞ rolde anlamlı: yeni rolün henüz
                ID'si yok, uç rol ID'siyle çalışıyor. */}
            {canUpdate && (
              <>
                <span className={styles.dot} />
                <button
                  className={styles.assignByTitleButton}
                  onClick={() => setAssignTitleTarget(currentRole)}
                  type="button"
                >
                  <Plus height={14} width={14} />
                  Ünvana göre tanımla
                </button>
              </>
            )}
            <span className={styles.dot} />
            <span>
              {totalSelectedCount}/
              {t("rolesAndPermissions.permissionCount", {
                count: totalPermCount,
              })}
            </span>
          </div>
        )}

        <div className={styles.matrixToolbar}>
          <div className={styles.matrixSearch}>
            <Input
              value={permissionSearch}
              onChange={(e) => setPermissionSearch(e.target.value)}
              placeholder={t("rolesAndPermissions.searchPermissions")}
            />
          </div>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDependencyInfoOpen(true)}
            title={t("rolesPage.showDependencyMap")}
          >
            Bağımlılık Haritası
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={toggleAll}
            disabled={editing.isDefault || (!canUpdate && !editing.isNew)}
          >
            {totalSelectedCount === totalPermCount
              ? t("rolesAndPermissions.deselectAll")
              : t("rolesAndPermissions.selectAll")}
          </Button>
        </div>

        <div className={styles.matrix} ref={matrixRef}>
          {filteredMatrix.length === 0 ? (
            <div className={styles.matrixEmpty}>
              {t("rolesAndPermissions.noMatch")}
            </div>
          ) : (
            matrixColumns.map((col, colIdx) => (
              <div key={colIdx} className={styles.matrixColumn}>
                {col.map((cat) => {
                  const keys = cat.permissions.map((p) => p.key);
                  const selectedCount = keys.filter((k) =>
                    editing.permissionKeys.has(k),
                  ).length;
                  const allSelected =
                    selectedCount === keys.length && keys.length > 0;
                  const someSelected = selectedCount > 0 && !allSelected;
                  const isExpanded =
                    permissionSearch.trim().length > 0 ||
                    expandedCategoryId === cat.id;
                  return (
                    <div
                      key={cat.id}
                      className={`${styles.category} ${isExpanded ? styles.categoryOpen : ""}`}
                    >
                      <div className={styles.categoryHeader}>
                        <button
                          type="button"
                          className={styles.categoryToggle}
                          onClick={() => toggleExpand(cat.id)}
                        >
                          <ArrowDown
                            width={16}
                            height={16}
                            className={`${styles.chev} ${isExpanded ? styles.chevOpen : ""}`}
                          />
                          <span className={styles.categoryLabel}>
                            {cat.label}
                          </span>
                        </button>
                        <div className={styles.categoryRight}>
                          <span className={styles.categoryCount}>
                            {selectedCount}/{keys.length}
                          </span>
                          <label className={styles.cbox}>
                            <input
                              type="checkbox"
                              checked={allSelected}
                              ref={(el) => {
                                if (el) el.indeterminate = someSelected;
                              }}
                              onChange={() => toggleCategory(keys)}
                              disabled={
                                editing.isDefault ||
                                (!canUpdate && !editing.isNew) ||
                                !keys.some((k) =>
                                  isPermissionAllowedForRoleType(
                                    k,
                                    editing.type,
                                  ),
                                )
                              }
                            />
                            <span className={styles.cboxBox} />
                          </label>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className={styles.categoryBody}>
                          {cat.permissions.map((p) => {
                            const checked = editing.permissionKeys.has(p.key);
                            const notAllowedForType =
                              !isPermissionAllowedForRoleType(
                                p.key,
                                editing.type,
                              );
                            return (
                              <label
                                key={p.key}
                                className={`${styles.permRow} ${notAllowedForType ? styles.permRowNotAllowed : ""}`}
                                title={
                                  notAllowedForType
                                    ? t(
                                        "rolesAndPermissions.learnerRestriction.notAllowed",
                                      )
                                    : undefined
                                }
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => togglePermission(p.key)}
                                  // İzin listesi dışı + işaretli (eski kayıt) ise
                                  // kaldırılabilsin diye açık bırakılır.
                                  disabled={
                                    editing.isDefault ||
                                    (!canUpdate && !editing.isNew) ||
                                    (notAllowedForType && !checked)
                                  }
                                />
                                <span className={styles.permRowLabel}>
                                  <span className={styles.permRowLabelText}>
                                    {p.label}
                                    <PermissionDependencyBadge
                                      permissionKey={p.key}
                                      getLabel={getPermissionLabel}
                                    />
                                  </span>
                                  {p.description && (
                                    <span className={styles.permRowDescription}>
                                      {p.description}
                                    </span>
                                  )}
                                  <small>{p.key}</small>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className={styles.detailFooter}>
          {!editing.isNew && canDelete && !editing.isDefault && currentRole && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeleteTarget(currentRole)}
              startIcon={<Delete width={16} height={16} />}
            >
              {t("rolesAndPermissions.deleteRole")}
            </Button>
          )}
          <div className={styles.detailFooterRight}>
            {editing.isNew && (
              <Button
                variant="text"
                color="secondary"
                onClick={() => setSelectedId(roles[0]?.id ?? null)}
                startIcon={<Close width={14} height={14} />}
              >
                {t("common.cancel")}
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              loading={isSaving}
              disabled={
                isSaving ||
                editing.isDefault ||
                (!canCreate && editing.isNew) ||
                (!canUpdate && !editing.isNew) ||
                !editing.name.trim()
              }
            >
              {editing.isNew
                ? t("rolesAndPermissions.createRole")
                : t("common.save")}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <Header title={t("rolesAndPermissions.title")} />

      <div className={styles.splitView}>
        <aside className={styles.sidePanel}>
          <div className={styles.sidePanelHeader}>
            <Input
              value={roleSearch}
              onChange={(e) => setRoleSearch(e.target.value)}
              placeholder={t("rolesAndPermissions.searchRoles")}
            />
            {canCreate && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddNew}
                startIcon={<Plus width={16} height={16} color="#fff" />}
              >
                {t("rolesAndPermissions.addButton")}
              </Button>
            )}
          </div>
          <div className={styles.roleList}>
            {isLoading && roles.length === 0 ? (
              <div className={styles.sideEmpty}>{t("common.loading")}</div>
            ) : filteredRoles.length === 0 ? (
              <div className={styles.sideEmpty}>
                {t("rolesAndPermissions.noRoles")}
              </div>
            ) : (
              filteredRoles.map(renderRoleCard)
            )}
          </div>
        </aside>

        <section className={styles.mainPanel}>
          {isLoadingDetail ? (
            <div className={styles.emptyState}>{t("common.loading")}</div>
          ) : (
            renderDetail()
          )}
        </section>
      </div>

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => !isDeleting && setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t("rolesAndPermissions.deleteConfirm.title")}
        message={t("rolesAndPermissions.deleteConfirm.message")}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        loading={isDeleting}
        confirmVariant="danger"
      />
      <AssignByTitleModal
        isOpen={!!assignTitleTarget}
        onAssigned={() => void fetchRoles()}
        onClose={() => setAssignTitleTarget(null)}
        role={assignTitleTarget}
      />
      <DependencyInfoPanel
        open={dependencyInfoOpen}
        onClose={() => setDependencyInfoOpen(false)}
      />
    </DashboardLayout>
  );
};

export default RolesAndPermissionsPage;
